import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { P as HARDCODED_P } from "@/components/nutrimart/data";
import { DOP } from "@/components/nutrimart/helpers";
import { readOfflineProducts, writeOfflineProducts } from "./useOfflineCache";

export interface DbProduct {
  id: string;
  name: string;
  brand: string | null;
  category: string | null;
  price: number;
  compare_at_price: number | null;
  sku: string | null;
  stock_quantity: number | null;
  image_url: string | null;
  images: string[] | null;
  description: string | null;
  nutrition_facts: any;
  tags: string[] | null;
  is_active: boolean | null;
  low_stock_threshold?: number | null;
  show_in_trending?: boolean | null;
  show_in_deals?: boolean | null;
  trending_order?: number | null;
  deals_order?: number | null;
}

/**
 * Fetches products from the database and merges with hardcoded enrichment data
 * matched by SKU. DB controls price, stock, active status; hardcoded provides
 * rich metadata.
 *
 * Offline support: hydrates from localStorage on mount so the catalog renders
 * instantly (and works without connection); refreshes from the network in the
 * background and persists the latest payload for the next offline visit.
 */
export function useProducts() {
  const [dbProducts, setDbProducts] = useState<DbProduct[]>(() => {
    return readOfflineProducts<DbProduct>() ?? [];
  });
  const [loading, setLoading] = useState(() => {
    // If we hydrated from cache, render immediately and refresh in background.
    return readOfflineProducts<DbProduct>() === null;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchProducts() {
      const { data, error: err } = await supabase
        .from("products")
        .select("id,name,brand,category,price,compare_at_price,sku,stock_quantity,image_url,images,description,nutrition_facts,tags,is_active,low_stock_threshold,show_in_trending,show_in_deals,slug,vendor_id,rating,review_count,itbis_exempt,itbis_rate,created_at,updated_at")
        .eq("is_active", true)
        .order("name");

      if (cancelled) return;
      if (err) {
        console.error("Failed to load products:", err);
        // Keep cached data; only surface error if we have nothing to show.
        if (dbProducts.length === 0) setError(err.message);
      } else if (data) {
        setDbProducts(data);
        writeOfflineProducts(data);
      }
      setLoading(false);
    }
    fetchProducts();

    // Force a fresh price refetch whenever the user lands on the products
    // listing (or returns to it via back/forward). This bypasses any locally
    // cached values so visitors on www.nutrimartrd.com always see the latest
    // prices from the backend, even if the offline cache was warm.
    const refreshOnProductsRoute = () => {
      const path = window.location.pathname;
      if (path === "/products" || path === "/productos") {
        try {
          // Drop the offline snapshot so the next reader can't serve stale prices.
          window.localStorage.removeItem("nutrimart_products_cache_v1");
          window.localStorage.removeItem("nutrimart_products_cache_meta_v1");
        } catch {
          /* ignore quota / privacy-mode errors */
        }
        fetchProducts();
      }
    };
    refreshOnProductsRoute();
    window.addEventListener("popstate", refreshOnProductsRoute);
    window.addEventListener("nm:route-change", refreshOnProductsRoute);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshOnProductsRoute();
    };
    document.addEventListener("visibilitychange", onVisible);

    // Realtime: refetch whenever the products table changes so admin edits,
    // stock updates, new products, etc. propagate instantly across the app
    // (including the Hero editor's product selector).
    const channel = supabase
      .channel("products_inventory_sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "products" },
        () => { fetchProducts(); }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      window.removeEventListener("popstate", refreshOnProductsRoute);
      window.removeEventListener("nm:route-change", refreshOnProductsRoute);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build a SKU → hardcoded product map for enrichment
  const skuMap = useMemo(() => {
    const map = new Map<string, (typeof HARDCODED_P)[number]>();
    for (const p of HARDCODED_P) {
      map.set(p.sku, p);
    }
    return map;
  }, []);

  // Merge: DB is source of truth for price/stock/active, hardcoded provides rich metadata
  const products = useMemo(() => {
    if (dbProducts.length === 0 && !loading) {
      // Fallback to hardcoded if DB is empty
      return HARDCODED_P;
    }

    return dbProducts.map((dbP) => {
      const hc = dbP.sku ? skuMap.get(dbP.sku) : undefined;
      const extras = buildDbExtras(dbP);
      // DB stores prices in DOP (matches the CSV uploaded by SKU).
      // The rest of the app treats product `pr`/`cpr` as USD and converts to
      // DOP at display time via fp(). Convert here so display matches the CSV 1:1.
      const prUsd = (dbP.price ?? 0) / DOP;
      const cprUsd = dbP.compare_at_price != null ? dbP.compare_at_price / DOP : null;

      if (hc) {
        // Merge: override price/stock from DB, keep rich metadata from hardcoded
        return {
          ...hc,
          // DB overrides
          dbId: dbP.id, // keep UUID for cart_items FK
          slug: (dbP as any).slug || undefined,
          pr: prUsd,
          cpr: cprUsd ?? hc.cpr,
          stk: dbP.stock_quantity ?? hc.stk,
          low_stock_threshold: dbP.low_stock_threshold ?? 10,
          // Admin-controlled dashboard placement (DB wins if explicitly set)
          feat: dbP.show_in_trending ?? (hc as any).feat ?? false,
          dealsPick: !!dbP.show_in_deals,
          trending_order: dbP.trending_order ?? 0,
          deals_order: dbP.deals_order ?? 0,
          // Multi-image support from DB (falls back to hardcoded img/imgs)
          img: dbP.image_url || (hc as any).img,
          imgs: (dbP.images && dbP.images.length > 0)
            ? dbP.images
            : ((hc as any).imgs || (dbP.image_url ? [dbP.image_url] : undefined)),
          // Admin-editable extras stored in nutrition_facts JSON
          ...(extras.nut ? { nut: { ...(hc as any).nut, ...extras.nut } } : {}),
          ...(extras.ing ? { ing: extras.ing } : {}),
          ...(extras.use ? { use: extras.use } : {}),
          ...(extras.warn ? { warn: extras.warn } : {}),
          ...(extras.alg ? { alg: extras.alg } : {}),
          ...(extras.timing ? { timing: extras.timing } : {}),
          ...(extras.ben ? { ben: extras.ben } : {}),
          ...(dbP.description ? { desc: { en: dbP.description, es: dbP.description } } : {}),
        };
      }

      // No hardcoded match — build a minimal product object from DB data
      const [enName, esName] = (dbP.name || "").split(" / ");
      return {
        id: dbP.sku ? parseInt(dbP.sku.replace(/\D/g, ""), 10) || dbP.id : dbP.id,
        dbId: dbP.id,
        slug: (dbP as any).slug || undefined,
        nm: { en: enName || dbP.name, es: esName || enName || dbP.name },
        br: dbP.brand || "",
        cat: dbP.category || "",
        pr: prUsd,
        cpr: cprUsd,
        rt: 0,
        rv: 0,
        img: dbP.image_url || "",
        imgs: dbP.images || (dbP.image_url ? [dbP.image_url] : []),
        sid: "",
        fm: { en: "", es: "" },
        fl: { en: "", es: "" },
        wt: "",
        sku: dbP.sku || "",
        stk: dbP.stock_quantity ?? 0,
        low_stock_threshold: dbP.low_stock_threshold ?? 10,
        feat: !!dbP.show_in_trending,
        dealsPick: !!dbP.show_in_deals,
        trending_order: dbP.trending_order ?? 0,
        deals_order: dbP.deals_order ?? 0,
        goals: (dbP.tags || []).filter((t: string) =>
          ["muscle", "weight", "recovery", "energy", "wellness", "immunity"].includes(t)
        ),
        diets: { en: [], es: [] },
        nut: extras.nut || {},
        ing: extras.ing || { en: "", es: "" },
        alg: extras.alg || { en: "None", es: "Ninguno" },
        ben: extras.ben || { en: [], es: [] },
        use: extras.use || { en: "", es: "" },
        warn: extras.warn || { en: "", es: "" },
        desc: { en: dbP.description || "", es: dbP.description || "" },
        interactions: [],
        timing: extras.timing || { en: "", es: "" },
      };
    });
  }, [dbProducts, loading, skuMap]);

  return { products, loading, error, dbProducts };
}

/**
 * Extract admin-editable extras from a DB product's nutrition_facts JSON.
 * Returns partial overrides matching the in-app product shape.
 */
function buildDbExtras(dbP: DbProduct) {
  const nf: any = dbP.nutrition_facts || {};
  const bi = (v: any) =>
    v == null || v === "" ? undefined : { en: String(v), es: String(v) };
  const biArr = (v: any) =>
    Array.isArray(v) && v.length > 0 ? { en: v, es: v } : undefined;
  const hasNumeric =
    nf.calories != null || nf.protein != null || nf.carbs != null ||
    nf.fat != null || nf.serving != null || nf.sugar != null ||
    nf.sodium != null || nf.fiber != null;
  return {
    nut: hasNumeric
      ? {
          sv: nf.serving ?? "",
          cal: nf.calories ?? 0,
          pro: nf.protein ?? 0,
          carb: nf.carbs ?? 0,
          fat: nf.fat ?? 0,
          sug: nf.sugar ?? 0,
          sod: nf.sodium ?? 0,
          fib: nf.fiber ?? 0,
        }
      : undefined,
    ing: bi(nf.ingredients),
    use: bi(nf.usage),
    warn: bi(nf.warnings),
    alg: bi(nf.allergens),
    timing: bi(nf.timing),
    ben: biArr(nf.benefits),
  };
}
